// ════════════════════════════════════════════════════════════
//  bannerLook.js — THE BANNERS' COLOURS AND caP'n's OWN BANNERS (Sun Dial "Banners" page, 2026-10-22)
// ════════════════════════════════════════════════════════════
//  caP'n: "have the ability to change the banner colors by the categories we gave or all at once or by player etc,
//  and a way to make banners myself and have them pop in and out".
//  The data lives in data/banners.json (edited by the Sun Dial; banners.original.json is the untouched factory copy).
//  RULES OF THIS FILE
//   • A blank colour means "leave it exactly as the game draws it" — so the factory file changes NOTHING on screen.
//   • The MOST SPECIFIC colour wins:  fine-tune (a category for one player)  →  the category  →  the player  →  everything.
//     When a category colour and a player colour both apply, `order` ("category" or "player") says which wins, and `prefer`
//     can flip that for one category ("player wins on the Rift of …") — caP'n: "a way for me to choose and swap if needed".
//   • GRACE (caP'n 2026-10-22): the glow around a player's picture on a banner that also engulfs their name. Left and right player
//     each have one here; a player's OWN colour (one they bought, later) wins over it. Blank = no grace, nothing changes.
//   • A colour that isn't #rrggbb is ignored (never reaches the page), a broken custom banner is skipped (never a crash).
//   • "Player" is yours / theirs. Banners that belong to nobody (the Rift, plans, a clash) skip the player rules.
//  Pure functions (no React), so tools can test them in Node.
// ════════════════════════════════════════════════════════════

export const HEX = /^#[0-9a-fA-F]{6}$/;
const ok = (c) => (typeof c === "string" && HEX.test(c) ? c : null);

// every banner category the game draws (the Sun Dial's page lists these from sundial.json)
export const GRACE_SURFACES = ["cutin", "log", "rift"];   // where Grace can show (the Sun Dial's Grace card has a switch for each)
export const KINDS = ["ult", "ascend", "enter", "rift", "tome", "impera", "uhlti", "ruined", "transcend", "plan", "clash", "custom"];

// what can make one of caP'n's own banners appear by itself. `sided`: it has a "yours / theirs".
export const WHEN = {
  ult: { label: "A U'hlti is cast", sided: true },
  ascend: { label: "A being transcends", sided: true },
  enter: { label: "A being steps in", sided: true },
  returns: { label: "A being returns (resurrected)", sided: true },
  rift: { label: "The Rift closes", sided: false },
};
export const BIG_SECS = [0.8, 8];     // ⟵ DIAL  how long a custom banner may stay (seconds, min and max)

/** the colour caP'n chose for a banner, or null (= draw it the usual way). mine: true / false / undefined (nobody's) */
export function colorFor(cfg, kind, mine) {
  if (!cfg || typeof cfg !== "object") return null;
  const sd = mine === true ? "mine" : mine === false ? "theirs" : null;
  const cat = ok(cfg.kinds && cfg.kinds[kind]), ply = sd && ok(cfg.sides && cfg.sides[sd]);
  const pick = (cfg.prefer && cfg.prefer[kind]) || cfg.order;
  return (sd && ok(cfg.fine && cfg.fine[`${kind}.${sd}`])) || (pick === "player" ? ply || cat : cat || ply) || ok(cfg.all) || null;
}

/** the Grace colour around a player's picture and name on a banner, or null. `own` = that player's own (bought) colour, if the game has one. */
export function graceFor(cfg, mine, own, surface) {
  const sd = mine === true ? "mine" : mine === false ? "theirs" : null;
  if (surface && cfg && cfg.graceOn && cfg.graceOn[surface] === false) return null;      // caP'n can switch Grace off for the cut-ins, the log or the Rift (all on to start: "synced")
  return ok(own) || (sd && cfg && cfg.grace && ok(cfg.grace[sd])) || null;
}

const clipText = (v, n) => (typeof v === "string" ? v.slice(0, n) : "");

/** caP'n's own banners, cleaned. Anything unusable is dropped. */
export function customBanners(cfg) {
  const out = [], seen = new Set();
  for (const c of (cfg && Array.isArray(cfg.custom) ? cfg.custom : [])) {
    if (!c || typeof c !== "object" || typeof c.id !== "string" || !/^[a-z0-9-]{1,24}$/.test(c.id) || seen.has(c.id)) continue;
    seen.add(c.id);
    const size = c.size === "small" ? "small" : "big";
    const side = size === "small" ? (c.side === "theirs" ? "theirs" : "mine") : (c.side === "theirs" || c.side === "both" ? c.side : "mine");
    const secs = Math.min(BIG_SECS[1], Math.max(BIG_SECS[0], Number.isFinite(+c.secs) ? +c.secs : 2));
    out.push({
      id: c.id, name: clipText(c.name, 40) || c.id, title: clipText(c.title, 40), sub: clipText(c.sub, 80),
      color: ok(c.color) || "", grace: ok(c.grace) || "", art: typeof c.art === "string" && /^[a-z0-9_]{1,24}$/.test(c.art) ? c.art : "",
      tier: c.tier === "champion" || c.tier === "lord" ? c.tier : "beb", size, side, secs, motion: c.motion === "slide" ? "slide" : "pop",
      when: (Array.isArray(c.when) ? c.when : []).filter((w, i, a) => WHEN[w] && a.indexOf(w) === i),
      whenSide: c.whenSide === "mine" || c.whenSide === "theirs" ? c.whenSide : "any",
    });
  }
  return out;
}

/** a custom banner as the queue (bannerQueue.js) wants it. It uses the SAME lanes, waiting line and clash as the built-in ones:
 *  big = the middle lane (kind "ult" geometry; "both" = the whole lane like the Rift), small = the low lane (kind "enter"). */
export function toQueued(c, key, hold) {
  const kind = c.side === "both" ? "rift" : c.size === "small" ? "enter" : "ult";
  return { key, kind, mine: c.side === "mine", title: c.title, sub: c.sub, life: c.secs, hold: !!hold, custom: c.id, motion: c.motion, color: c.color || "", grace: c.grace || "", line: c.art || undefined, tier: c.art ? c.tier : undefined };
}

/** does this game event show this custom banner? (side: did it happen on caP'n's side?) */
export function firesOn(c, when, mine) {
  if (!c.when.includes(when)) return false;
  if (!WHEN[when].sided || c.whenSide === "any") return true;
  return c.whenSide === (mine ? "mine" : "theirs");
}
